import layout from './agentLayout.module.css';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Form, Input, Typography } from 'antd';
import type { FormInstance } from 'antd';
import PhoneInput from 'react-phone-input-2';
import { RouteConditionSelect } from './RouteConditionSelect';
import { EscalationRuleRow } from './EscalationRuleRow';
import {
  validateDefaultPhone,
  validateEscalationRules,
} from './escalationValidators';
import type {
  AgentDrawerFormValues,
  EscalationRule,
  PhoneTree,
} from '../types';

const { Paragraph } = Typography;

const DefaultPhoneInput: React.FC<{
  value?: string;
  onChange?: (val: string) => void;
  disabled?: boolean;
}> = ({ value: injectedValue, onChange, disabled }) => {
  const [phone, setPhone] = useState((injectedValue || '').replace(/^\+/, ''));

  useEffect(() => {
    setPhone((injectedValue || '').replace(/^\+/, ''));
  }, [injectedValue]);

  return (
    <PhoneInput
      country='us'
      value={phone}
      disableCountryCode={!phone}
      onChange={(val) => {
        setPhone(val);
        onChange?.(val ? `+${val}` : '');
      }}
      disabled={disabled}
      containerClass={
        disabled ? 'agentPhone agentPhone--primary' : 'agentPhone'
      }
      inputClass={
        disabled
          ? 'agentPhone__input agentPhone__input--primary'
          : 'agentPhone__input'
      }
      buttonClass={
        disabled
          ? 'agentPhone__button agentPhone__button--primary'
          : 'agentPhone__button'
      }
      dropdownClass='agentPhone__dropdown'
    />
  );
};

export const EscalationSection: React.FC<{
  loadingDropdowns: boolean;
  form: FormInstance<AgentDrawerFormValues>;
  escalateWhenOptions: Array<{ value: string; label: string }>;
  isDefaultAgent: boolean;
  readOnly?: boolean;
}> = ({
  loadingDropdowns,
  form,
  escalateWhenOptions,
  isDefaultAgent,
  readOnly = false,
}) => {
  const lockedForDefault = readOnly || isDefaultAgent;
  const [rules, setRules] = useState<EscalationRule[]>([]);
  const initializedRef = useRef(false);

  const formRules = Form.useWatch('escalation_rules', form) as
    EscalationRule[] | undefined;
  const menu: PhoneTree | undefined = Form.useWatch('phone_tree', form);

  useEffect(() => {
    if (
      !initializedRef.current &&
      Array.isArray(formRules) &&
      formRules.length > 0
    ) {
      initializedRef.current = true;
      setRules(formRules);
    }
  }, [formRules]);

  const defaultOptions = escalateWhenOptions;

  const syncRules = (next: EscalationRule[]) => {
    setRules(next);
    form.setFieldValue('escalation_rules', next);
    form.validateFields(['escalation_rules']).catch(() => {});
  };

  const addRule = () => {
    syncRules([
      ...rules,
      {
        id: crypto.randomUUID(),
        name: `Rule ${rules.length + 1}`,
        phone_number: '',
        extension: '',
        escalate_when: [],
      },
    ]);
  };

  const updateRule = (updated: EscalationRule) => {
    syncRules(rules.map((r) => (r.id === updated.id ? updated : r)));
  };

  const deleteRule = (id: string) => {
    const menu = form.getFieldValue('phone_tree');
    if (menu)
      form.setFieldValue('phone_tree', {
        ...menu,
        options: menu.options.map(
          (option: { id: string; label: string; destination: string }) =>
            option.destination === id ? { ...option, destination: '' } : option
        ),
      });
    const next = rules.filter((r) => r.id !== id);
    syncRules(next);
    if (next.length === 0) {
      form.setFields([
        { name: 'escalation_phone_number', errors: [] },
        { name: 'escalation_rules', errors: [] },
      ]);
    }
  };

  return (
    <>
      <Form.Item
        name='escalation_rules'
        hidden
        dependencies={['phone_tree']}
        rules={[
          {
            validator: (_, value: EscalationRule[]) =>
              validateEscalationRules(
                value,
                isDefaultAgent,
                form.getFieldValue('phone_tree')
              ),
          },
        ]}
      >
        <input type='hidden' />
      </Form.Item>

      <Form.Item shouldUpdate noStyle>
        {() => {
          const showErrors = form.getFieldError('escalation_rules').length > 0;
          return (
            <>
              {rules.map((rule) => (
                <EscalationRuleRow
                  key={rule.id}
                  rule={rule}
                  form={form}
                  escalateWhenOptions={escalateWhenOptions}
                  loadingDropdowns={loadingDropdowns}
                  isDefaultAgent={isDefaultAgent}
                  readOnly={lockedForDefault}
                  required
                  showErrors={showErrors}
                  onChange={updateRule}
                  onDelete={deleteRule}
                />
              ))}

              {!lockedForDefault && rules.length > 0 && (
                <Col span={24} style={{ padding: 0 }}>
                  <Button
                    onClick={addRule}
                    className='escalationAddRuleButton'
                    style={{
                      width: '100%',
                      height: 28,
                      borderRadius: 14,
                      border: '1px solid var(--gray-600)',
                      background: 'transparent',
                      color: 'var(--text-primary)',
                      boxShadow: 'none',
                    }}
                  >
                    + Add escalation rule
                  </Button>
                </Col>
              )}
            </>
          );
        }}
      </Form.Item>

      {!isDefaultAgent && (
        <Col span={24} style={{ padding: 0 }}>
          <Form.Item
            label={
              menu?.enabled
                ? 'Route / Escalate when caller...'
                : 'Escalate when'
            }
            name='escalate_when'
          >
            <RouteConditionSelect
              form={form}
              destination='default'
              options={defaultOptions}
              disabled={readOnly || loadingDropdowns}
            />
          </Form.Item>
        </Col>
      )}

      <Col span={24} style={{ padding: 0 }}>
        <div className={layout.phoneRow}>
          <Form.Item
            label={
              rules.length === 0 || menu?.enabled
                ? 'Default escalation number (optional)'
                : 'Default escalation number'
            }
            name='escalation_phone_number'
            required={rules.length > 0}
            className='agentFormNoMargin'
            style={{ flex: 1 }}
            rules={[
              {
                validator: (_, value?: string) =>
                  validateDefaultPhone(value, rules.length, menu),
              },
            ]}
          >
            <DefaultPhoneInput disabled={lockedForDefault} />
          </Form.Item>
          {(!lockedForDefault ||
            form.getFieldValue('escalation_phone_number_extension')) && (
            <Form.Item
              name='escalation_phone_number_extension'
              label=' '
              colon={false}
              style={{ width: 100 }}
            >
              <Input
                className={
                  lockedForDefault
                    ? 'agentInput agentInput--readonly'
                    : 'agentInput'
                }
                disabled={lockedForDefault}
                placeholder='Extension'
                maxLength={20}
              />
            </Form.Item>
          )}
        </div>
        <Paragraph className='agentFormHelpText'>
          {rules.length === 0
            ? 'Transfer to this number based on escalation conditions set above'
            : 'If no rule above applies, connect with this number'}
        </Paragraph>
      </Col>

      {!lockedForDefault && rules.length === 0 && (
        <Col span={24} style={{ padding: 0 }}>
          <Button
            onClick={addRule}
            className='escalationAddRuleButton'
            style={{
              width: '100%',
              height: 28,
              borderRadius: 14,
              border: '1px solid var(--gray-600)',
              background: 'transparent',
              color: 'var(--text-primary)',
              boxShadow: 'none',
            }}
          >
            + Add escalation rule
          </Button>
        </Col>
      )}
    </>
  );
};
